import React, { useState, useEffect } from 'react';
import { Tooltip, CircularLoader } from '@dhis2/ui';

// Data element for objective weight
const WEIGHT_DATA_ELEMENT = 'yfb9RCRpMB2';

function AddObjectiveButton({
  onAdd,
  reportingToObjectives = [],
  myObjectives = [],
  goalItems = [],
  currentUserPosition = null,
  loading = false,
  goalsFullyLoaded = false,
  managerObjectivesLoaded = false,
  protectedObjectives = new Set(),
  childCounts = {},
  creationAllowed = true,
}) {
  const [hovered, setHovered] = useState(false);

  const isLoading = loading || !goalsFullyLoaded || !managerObjectivesLoaded;

  // Determine if user is top-level (CEO/Admin) - NO reporting-to attribute
  let isTopLevelUser = false;
  if (currentUserPosition?.attributes) {
    const reportingToAttr = currentUserPosition.attributes.find(
      attr => attr.attribute === 'SjP47qi3JeB'
    );
    isTopLevelUser = !reportingToAttr?.value;
  }

  // ---- Derived counts (used by lock, text, and badge) ----

  // Total weight of the user's own objectives
  const myTotalWeight = myObjectives.reduce((sum, obj) => {
    const rawWeight =
      obj.weight ??
      obj.dataValues?.find(dv => dv.dataElement === WEIGHT_DATA_ELEMENT)?.value ??
      0;
    const w = parseInt(rawWeight, 10);
    return sum + (isNaN(w) ? 0 : w);
  }, 0);

  // User's own objectives awaiting approval (pending OR returned)
  const myPendingCount = myObjectives.filter(
    obj => obj.approvalStatus === 'pending' || obj.approvalStatus === 'returned'
  ).length;

  // User's own approved objectives
  const myApprovedCount = myObjectives.filter(
    obj => obj.approvalStatus === 'approved'
  ).length;

  // Manager's approved objectives (used only for prerequisite check)
  const approvedManagerCount = reportingToObjectives.filter(
    obj => obj.approvalStatus === 'approved'
  ).length;

  useEffect(() => {
    console.log('🔍 [AddObjectiveButton] Props updated:', {
      myObjectivesCount: myObjectives.length,
      myTotalWeight,
      myPendingCount,
      myApprovedCount,
      approvedManagerCount,
      isTopLevelUser,
      creationAllowed,
      goalsFullyLoaded,
      managerObjectivesLoaded,
    });
  }, [
    myObjectives,
    myTotalWeight,
    myPendingCount,
    myApprovedCount,
    approvedManagerCount,
    isTopLevelUser,
    creationAllowed,
    goalsFullyLoaded,
    managerObjectivesLoaded,
  ]);

  // ✅ LOCK LOGIC
   const getLockStatus = () => {
    // ============================================================
    // TOP-LEVEL USERS (CEO / Director)
    // ============================================================
    if (isTopLevelUser) {
      if (goalItems.length === 0) {
        return {
          locked: true,
          reason:
            'No company goals available for the current fiscal year. Please create company goals first.',
          type: 'prerequisite',
        };
      }
      if (myTotalWeight >= 100) {
        return {
          locked: true,
          reason: `Your objectives already total ${myTotalWeight}%. You cannot add more until you reduce or remove an existing one.`,
          type: 'weight_complete',
        };
      }
      return { locked: false, reason: '' };
    }

    // ============================================================
    // NON-TOP-LEVEL USERS
    // ============================================================

    // ✅ THE ONLY HARD RULE: weight cap (applies to everyone, always)
    if (myTotalWeight >= 100) {
      return {
        locked: true,
        reason: `Your objectives already total ${myTotalWeight}%. You cannot add more until you reduce or remove an existing one.`,
        type: 'weight_complete',
      };
    }

    // ✅ Prerequisite gates — only apply if user has NO objectives yet
    //    (Once they've created at least one, they clearly cleared these gates.)
    if (myObjectives.length === 0) {
      // No manager objectives → nothing to align to
      if (reportingToObjectives.length === 0) {
        return {
          locked: true,
          reason:
            'Your manager has not created any objectives for this fiscal year. Please wait for your manager to create objectives first.',
          type: 'prerequisite',
        };
      }

      // Manager objectives exist but none approved yet
      if (approvedManagerCount === 0) {
        return {
          locked: true,
          reason:
            'Your manager has not yet finalized their objectives. Please wait for approval before creating your own.',
          type: 'manager_approval',
        };
      }
    }

    // All clear — user has objectives, weight < 100%, can add more
    return { locked: false, reason: '' };
  };  

  let allowed = true;
  let reason = '';

  if (!isLoading) {
    const lockStatus = getLockStatus();
    allowed = !lockStatus.locked;
    reason = lockStatus.reason;
  }

  console.log('🔍 [AddObjectiveButton] Final State:', {
    isLoading,
    allowed,
    reason,
    isTopLevelUser,
    myTotalWeight,
    myPendingCount,
    myApprovedCount,
    approvedManagerCount,
  });

  const baseStyle = {
    position: 'fixed',
    bottom: '30px',
    right: '30px',
    zIndex: 1000,
  };

  const getButtonColor = () => {
    if (isLoading) return '#9e9e9e';
    if (!allowed) return '#9e9e9e';
    return '#1c9420';
  };

  const buttonStyle = {
    borderRadius: hovered ? '24px' : '50%',
    border: 'none',
    width: hovered ? '200px' : '50px',
    height: '50px',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: getButtonColor(),
    color: '#fff',
    cursor: !isLoading && allowed ? 'pointer' : 'not-allowed',
    boxShadow: '0 8px 16px rgba(122, 118, 118, 1)',
    transition: 'width 0.3s ease, border-radius 0.3s ease, padding 0.3s ease',
    overflow: 'hidden',
    padding: hovered ? '0 20px' : '0',
    opacity: isLoading ? 0.7 : 1,
    whiteSpace: 'nowrap',
  };

  const handleClick = () => {
    if (!isLoading && allowed && onAdd) {
      onAdd();
    } else if (!allowed && reason) {
      alert(`❌ Cannot Add Objective\n\n${reason}`);
    }
  };

  const getButtonText = () => {
    if (isLoading) return '⏳';

    // ---- Top-level users ----
    if (isTopLevelUser) {
      if (!hovered) return '+';
      const remaining = Math.max(0, 100 - myTotalWeight);
      if (myTotalWeight >= 100) return '🔒 Weight Complete';
      if (myTotalWeight > 0) return `+ Add (${remaining}% left)`;
      return '+ Add Objective';
    }

    // ---- Non-top-level users ----
    if (!hovered) return allowed ? '+' : '🔒';

    if (!allowed) return '🔒 Locked';

    // Hover text when allowed
    const remaining = Math.max(0, 100 - myTotalWeight);

    if (myPendingCount > 0) {
      return `⏳ ${myPendingCount} Awaiting Approval`;
    }
    if (myTotalWeight >= 100) {
      return '🔒 Weight Complete';
    }
    if (myTotalWeight > 0) {
      return `+ Add (${remaining}% left)`;
    }
    return '+ Add Objective';
  };

  // ✅ BADGE
  const getBadge = () => {
    // Only show badge when not hovering
    if (hovered) return null;

    const badgeStyle = {
      position: 'absolute',
      top: '-5px',
      right: '-5px',
      borderRadius: '50%',
      width: '20px',
      height: '20px',
      fontSize: '10px',
      fontWeight: 'bold',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    };

    // ---- Top-level users ----
    // Show nothing special; they auto-approve so no "pending" concept applies.
    // (If you want to show something for top-level users, add it here.)
    if (isTopLevelUser) {
      return null;
    }

    // ---- Non-top-level users ----
    // Priority 1: pending / returned objectives → amber badge with count
    if (myPendingCount > 0) {
      return (
        <span
          style={{ ...badgeStyle, backgroundColor: '#ffc107', color: '#333' }}
          title={`${myPendingCount} of your objective(s) awaiting approval`}
        >
          {myPendingCount}
        </span>
      );
    }

    // Priority 2: user has objectives and all are approved → green ✓
    if (myObjectives.length > 0 && myApprovedCount === myObjectives.length) {
      return (
        <span
          style={{ ...badgeStyle, width: '18px', height: '18px', backgroundColor: '#4CAF50', color: 'white' }}
          title="All objectives approved"
        >
          ✓
        </span>
      );
    }

    // Otherwise: no badge
    return null;
  };

  if (isLoading) {
    return (
      <div style={baseStyle}>
        <div style={buttonStyle}>
          <CircularLoader small />
        </div>
      </div>
    );
  }

  const buttonElement = (
    <div style={{ position: 'relative' }}>
      <button
        onClick={handleClick}
        style={buttonStyle}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        disabled={!allowed}
      >
        {getButtonText()}
      </button>
      {getBadge()}
    </div>
  );

  if (!allowed) {
    return (
      <div style={baseStyle}>
        <Tooltip content={reason} placement="left">
          {buttonElement}
        </Tooltip>
      </div>
    );
  }

  return <div style={baseStyle}>{buttonElement}</div>;
}

export default AddObjectiveButton;